// @ts-nocheck -- Gadget Client types are stale; will resolve after ggt push regenerates types
/**
 * File: web/routes/_auth.sign-up.tsx
 * Purpose: Magic link + Google OAuth sign-up with firstName capture.
 *          Powered by @barrykilby/auth.
 *
 * Referral plan Section 3b: if the URL carries ?ref=MANOS-XXXXXXXX we
 * forward it to the magic-link path (where it rides user.signUp params
 * on account creation) and stash it in sessionStorage for the Google
 * OAuth path -- that path drops query params through the redirect, so
 * /_app.tsx reads the key post-callback and POSTs to
 * /api/referral-attribution.
 */

import type { ActionFunctionArgs } from "react-router";
import { useActionData, useNavigation, useSearchParams } from "react-router";
import { MagicLinkAuth, sendMagicLinkForEmail } from "@barrykilby/auth";

const PENDING_REF_STORAGE_KEY = "manos.pendingRef";
// MANOS- + 8 hex chars. Pattern mirrors api/lib/referral/normaliseRefCode.
const REF_CODE_PATTERN = /^MANOS-[0-9A-F]{8}$/;

function normaliseRefCode(raw: string | null): string | null {
  if (!raw) return null;
  const s = raw.trim().toUpperCase();
  return REF_CODE_PATTERN.test(s) ? s : null;
}

export async function action({ request, context }: ActionFunctionArgs) {
  const formData = await request.formData();
  const email = formData.get("email") as string;
  const firstName = (formData.get("firstName") as string | null)?.trim() || "";
  const refCodeRaw = (formData.get("refCode") as string | null) ?? null;
  const refCode = normaliseRefCode(refCodeRaw);
  const inviteTokenRaw = (formData.get("inviteToken") as string | null) ?? null;
  const inviteToken =
    inviteTokenRaw && inviteTokenRaw.length > 0 ? inviteTokenRaw : null;

  const { randomUUID } = await import("node:crypto");

  // When there's an invite token (subcontract or team invite), bypass
  // the shared-lib helper -- it doesn't accept inviteToken and the
  // previous "double-send to overwrite" workaround was racing Mailpit
  // (the test pulled the FIRST email, which had no invite=, before
  // the second arrived). Inline the find-or-create + sendMagicLink
  // dance so the email is sent ONCE with the invite param attached.
  if (inviteToken) {
    const normalised = email.trim().toLowerCase();
    if (!normalised) {
      return { ok: false, error: "Please enter a valid email address." };
    }
    try {
      const adminApi = context.api.actAsAdmin;
      const existing = await adminApi.user.findMany({
        filter: { email: { equals: normalised } },
        select: { id: true, firstName: true },
        first: 1,
      });
      let userId: string;
      if (existing[0]?.id) {
        userId = existing[0].id;
        if (firstName && !existing[0].firstName) {
          await adminApi.user.update(userId, { firstName });
        }
      } else {
        const newUser = await adminApi.internal.user.create({
          email: normalised,
          emailVerified: false,
          roles: ["signed-in"],
          uuid: randomUUID(),
          ...(firstName ? { firstName } : {}),
          ...(refCode ? { refCode } : {}),
        });
        userId = newUser.id;
      }
      await (adminApi.user as any).sendMagicLink(userId, { inviteToken });
      return { ok: true, email: normalised };
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.";
      return { ok: false, error: message };
    }
  }

  const result = await sendMagicLinkForEmail(context, email, {
    onCreateUser: () => ({
      uuid: randomUUID(),
      ...(firstName ? { firstName } : {}),
      ...(refCode ? { refCode } : {}),
    }),
  });

  // If user already existed and has no firstName set, update it.
  if (result.ok && firstName) {
    try {
      const adminApi = context.api.actAsAdmin;
      const existing = await adminApi.user.findMany({
        filter: { email: { equals: result.email } },
        select: { id: true, firstName: true },
        first: 1,
      });
      if (existing[0] && !existing[0].firstName) {
        await adminApi.user.update(existing[0].id, { firstName });
      }
    } catch {
      // Non-critical, continue
    }
  }

  return result;
}

export default function SignUp() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const isSubmitting = navigation.state === "submitting";

  const search = searchParams.toString() ? `?${searchParams.toString()}` : "";
  // Force Google's account-picker even on sign-up so a user who's
  // already signed into Google with one identity can still pick a
  // different one. Matches the sign-in path -- both buttons hand off
  // the same prompt directive.
  const oauthParams = new URLSearchParams(searchParams);
  oauthParams.set("prompt", "select_account");
  const oauthSearch = `?${oauthParams.toString()}`;
  const refCode = normaliseRefCode(searchParams.get("ref"));
  const inviteToken = searchParams.get("invite") || "";

  // Stash the referral code in sessionStorage so the Google OAuth path
  // can read it after the redirect clears URL params. /_app.tsx drains
  // the key and POSTs to /api/referral-attribution. No-op when the URL
  // doesn't carry a valid ref code.
  //
  // Done via an inline <script> rendered in the SSR HTML (see below)
  // so the stash lands BEFORE React hydrates -- useEffect lagged by
  // a hydration tick and Playwright's evaluate() raced it.

  return (
    <>
      {/* Inline stash script: runs BEFORE React hydrates so the
          referral code is in sessionStorage by the time the Google
          OAuth button can be clicked or a Playwright spec reads it.
          The useEffect path raced hydration; see issue #85. */}
      {refCode ? (
        <script
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `try{sessionStorage.setItem(${JSON.stringify(PENDING_REF_STORAGE_KEY)},${JSON.stringify(refCode)});}catch(e){}`,
          }}
        />
      ) : null}
      <MagicLinkAuth
        appName="Manos"
        mode="sign-up"
        googleOAuthHref={`/auth/google/start${oauthSearch}`}
        crossLinkHref={`/sign-in${search}`}
        actionData={actionData}
        isSubmitting={isSubmitting}
        showAppNameInHeading={false}
        extraFields={
          <div className="space-y-1.5">
            <label
              htmlFor="firstName"
              className="text-sm font-medium leading-none text-foreground"
            >
              First name
            </label>
            <input
              id="firstName"
              type="text"
              name="firstName"
              placeholder="Your first name"
              autoComplete="given-name"
              autoFocus
              className="flex h-11 w-full rounded-md border border-border bg-secondary px-3 py-2 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            {refCode ? (
              <>
                <input type="hidden" name="refCode" value={refCode} />
                <p className="text-xs text-muted-foreground">
                  Invited by someone? You'll be credited as their referral.
                </p>
              </>
            ) : null}
            {inviteToken ? (
              <input
                type="hidden"
                name="inviteToken"
                value={inviteToken}
                data-testid="subcontract-invite-token"
              />
            ) : null}
          </div>
        }
      />
    </>
  );
}
